// Sensor fusion for heavy machinery blog post — BLOG-006
import { ArrowRight } from "lucide-react";
import Nav from "../../components/Nav";
import Footer from "../../components/Footer";
import PostHero from "../../components/PostHero";
import Reveal from "../../components/Reveal";

export default function SensorFusionHeavyMachinery() {
  return (
    <div className="bg-black">
      <Nav route="inner" />

      <PostHero
        category="Machinery"
        title="Sensor fusion for heavy machinery: a 12-week path to anomaly detection"
        author="Industry · Machinery"
        date="March 2026"
        readTime="6 min read"
        excerpt="Every machine in your fleet generates data from a dozen sources that were
         never designed to talk to each other. Here is how we fuse telematics, CAN bus
         and maintenance history into one anomaly-detection model — and what predictive
         maintenance looks like in the first 90 days."
      />

      <main>
        <div className="max-w-3xl mx-auto px-6 py-12">

          <Reveal>
            <h2 className="font-instrument text-2xl md:text-3xl text-white tracking-tight mt-2">
              The data fragmentation problem
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              A large excavator produces data from its CAN bus, its telematics
              unit, its on-board diagnostics and its hydraulic pressure sensors.
              None of those systems were designed to work together. Each emits a
              different format, at a different frequency, with a different definition
              of what counts as an alarm. If you manage a mixed fleet, multiply
              that by every OEM you run. This is the single biggest blocker we see
              in <a href="/industries/machinery.html" className="text-white/90 underline decoration-white/30 underline-offset-4 hover:decoration-white/70 transition-colors">AI work for heavy machinery</a> across
              Australia and NZ — not the models, the data.
            </p>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              Most machinery businesses bridge the gap with experienced
              technicians who know, intuitively, how to read signals across
              systems. That knowledge is valuable and hard to retain. When a senior
              technician leaves, years of pattern recognition walk out the door —
              and no documented maintenance procedure captures it. Sensor fusion is
              how you embed that judgement in software before it disappears.
            </p>
          </Reveal>

          <div className="hairline my-12" />

          <Reveal>
            <h2 className="font-instrument text-2xl md:text-3xl text-white tracking-tight mt-2">
              What sensor fusion actually means
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              Sensor fusion combines data from multiple sources into a single
              representation of the machine's operating state. The goal is not to
              collect more data. It is to build a unified context in which an
              anomaly-detection model can find patterns that are invisible when
              each signal is read in isolation.
            </p>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              A bearing failure, for example, may produce a small vibration
              signature, a slight temperature rise and a marginal change in power
              draw. None of those trips an alarm on its own, but together they are
              a clear early warning. A fused model sees the pattern weeks before
              it becomes a failure event — which is the difference between a
              scheduled component swap and an unplanned haul-road recovery. That
              is the practical case for predictive maintenance: it converts
              surprise downtime into planned work.
            </p>
          </Reveal>

          <div className="hairline my-12" />

          <Reveal>
            <h2 className="font-instrument text-2xl md:text-3xl text-white tracking-tight mt-2">
              The 12-week implementation path
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              Weeks 1 and 2 are sensor inventory and data quality assessment.
              We map every data source available for each asset class, assess
              completeness and continuity, and identify the gaps. In most
              engagements telematics data is good, CAN bus data is available but
              poorly documented, and maintenance records need normalisation before
              they can be used. You get a written readout either way — what is
              detectable with your data today, and what is not yet.
            </p>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              Weeks 3 to 6 are model development. We build a normal operating
              envelope for each asset type from historical data and train the
              anomaly-detection model against known failure events in your
              maintenance history. Your most experienced technicians are part of
              this step — they validate model outputs against their own pattern
              knowledge before we proceed. This is standard practice in
              our <a href="/services/ai-integration.html" className="text-white/90 underline decoration-white/30 underline-offset-4 hover:decoration-white/70 transition-colors">AI integration engagements</a>:
              the model has to earn the floor's agreement before it earns
              production traffic.
            </p>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              Weeks 7 to 12 take the system from prototype to production. We
              connect model outputs to the workflows your team already uses —
              typically a daily exception report, a mobile alert for shift
              supervisors and a dashboard for your maintenance coordinator. The
              model runs in shadow mode alongside your existing process while we
              measure true-positive and false-positive rates. Nothing goes live
              until those numbers are evidenced, so the risk of a noisy launch is
              removed before your operators ever see an alert.
            </p>
          </Reveal>

          <div className="hairline my-12" />

          <Reveal>
            <h2 className="font-instrument text-2xl md:text-3xl text-white tracking-tight mt-2">
              Designing for operator trust
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              Anomaly-detection models fail in the field when operators do not
              trust them, and the most common reason for distrust is unexplained
              output. An alert that says "anomaly detected, confidence 0.87" gives
              an operator nothing to act on. An alert that says "early signs of
              hydraulic pump cavitation on Unit 14 — recommend inspection before
              next shift" is actionable and understandable.
            </p>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              We write model outputs in plain language built from the
              terminology your technicians already use. That requires working with
              your team, not just your data. The language the model speaks shapes
              how operators judge its reliability — and operator adoption, not
              model accuracy, is what determines whether an AI transformation
              programme survives contact with a working fleet.
            </p>
          </Reveal>

          <div className="hairline my-12" />

          <Reveal>
            <h2 className="font-instrument text-2xl md:text-3xl text-white tracking-tight mt-2">
              What the first 90 days looks like
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              In the first 90 days, expect to catch two to three failure events
              before they cause unplanned downtime on your highest-utilisation
              assets. The false-positive rate will be higher than you want in the
              first month and will improve materially by month three as the model
              accumulates operational feedback. We see the same curve whether the
              fleet is metro civil plant or remote
              WA <a href="/industries/mining.html" className="text-white/90 underline decoration-white/30 underline-offset-4 hover:decoration-white/70 transition-colors">mining equipment</a> running
              on constrained site connectivity.
            </p>
            <p className="text-white/70 text-base leading-relaxed mt-4">
              The metric that predicts long-term success is not downtime
              reduction. It is whether your maintenance planners are using model
              outputs in their daily planning by the end of month three. If they
              are, the system will sustain its value and you can scale it across
              asset classes. If they are not, fix the integration gap before
              measuring outcomes — more model accuracy will not save a workflow
              nobody uses.
            </p>
          </Reveal>

        </div>
      </main>

      <section className="bg-black px-6 pb-24 mt-4">
        <div className="max-w-2xl mx-auto liquid-glass rounded-3xl p-10 text-center">
          <h3 className="font-instrument text-3xl text-white">
            Ready to scope anomaly detection for your fleet?
          </h3>
          <p className="text-white/60 text-base mt-4">
            We run a two-week sensor data discovery — we audit your telematics,
            CAN bus and maintenance records, and tell you exactly what predictive
            maintenance is possible with the data you already have. No commitment
            required.
          </p>
          <a
            href="/#contact"
            className="mt-8 inline-flex items-center gap-2 rounded-full px-8 py-3 bg-white text-black text-sm font-medium hover:bg-white/90 transition-colors"
          >
            Book a sensor data discovery <ArrowRight size={16} />
          </a>
          <div>
            <a href="/industries/machinery.html" className="mt-4 inline-flex items-center gap-2 liquid-glass rounded-full px-8 py-3 text-white text-sm font-medium hover:bg-white/5 transition-colors">
              AI for Heavy Machinery <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
